import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import logo from '../assets/logo.png';
import { toast } from 'sonner';

import Turnstile from 'react-turnstile'; const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileKey, setTurnstileKey] = useState(0); // Used to force remount/reset
  const turnstileRef = useRef();

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const isRedirected = Boolean(location.state?.from);

  useEffect(() => {
    // Logging only on mount
    console.log("Login component mounted. Redirect path:", from);

    // Handle Safari bfcache (back-forward cache)
    const handlePageShow = (event) => {
      if (event.persisted) {
        setCaptchaToken(null);
        // Don't hide captcha if it was shown, just reset token
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [from]);

  // ---------------------------
  // EMAIL + PASSWORD LOGIN
  // ---------------------------
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // 1. Try to login
    const { error: loginError } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken },
    });

    if (loginError) {
      // 2. Check if error is related to Captcha/Security
      // Supabase returns "captcha verification process failed" if token is missing/invalid when required
      if (loginError.message.includes("captcha") || loginError.message.includes("security")) {
        setError("Security check required. Please complete the captcha below.");
        toast.error("Security check required");
        setShowCaptcha(true);
        setLoading(false);
        setTurnstileKey(prev => prev + 1); // Force remount
        setCaptchaToken(null);
        return;
      }

      setError(loginError.message);
      toast.error(loginError.message);
      setLoading(false);
      setTurnstileKey(prev => prev + 1); // Force remount
      setCaptchaToken(null);

      // Fallback: If user fails 2+ times, show captcha just in case
      // (This logic could be enhanced with a counter ref, but keeping it simple for now)
      return;
    }

    console.log("Login successful!");
    toast.success("Welcome back! Redirecting...");
    console.log("Redirecting to:", from);

    setTimeout(() => {
      navigate(from, { replace: true });
    }, 100);

    setLoading(false);
  };

  // ---------------------------
  // GOOGLE LOGIN (SUPABASE)
  // ---------------------------
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");

    const destination = from && from !== '/' ? from : '/exercises';
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}${destination}`,
      },
    });

    if (error) {
      toast.error(error.message);
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0a1220] p-4 transition-colors duration-300">
      <div className="w-full max-w-5xl bg-white dark:bg-[#162032] rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-800 grid md:grid-cols-2 transition-colors duration-300">

        {/* LEFT COLUMN: Slick Promo */}
        <div className="hidden md:flex flex-col justify-center items-center text-center p-12 relative overflow-hidden bg-[#162032] bg-gradient-to-br from-blue-900/20 to-purple-900/20">
          {/* Background decoration */}
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-blue-500/20 rounded-full blur-[80px]"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-orange-500/10 rounded-full blur-[100px]"></div>

          <div className="relative z-10 flex flex-col items-center">
            <Link to="/" className="mb-8 block">
              <img src={logo} alt="DataHelix Logo" className="h-24 w-auto drop-shadow-2xl hover:scale-105 transition-transform" />
            </Link>
            <h2 className="text-3xl font-bold text-white mb-6">
              Welcome back to <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">DataHelix</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8 leading-relaxed">
              Continue your journey to mastering data analytics. Access premium SQL cases, track your progress, and build your portfolio.
            </p>

            <div className="flex items-center gap-4 text-sm text-gray-500 font-mono">
              <div className="flex -space-x-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gray-800 border-2 border-[#162032]"></div>
                ))}
              </div>
              <span>Join 500+ professionals</span>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Form */}
        <div className="p-8 md:p-12 flex flex-col justify-center">

          {/* Logo - Mobile Only */}
          <div className="mb-6 md:hidden">
            <Link to="/" className="inline-block">
              <img src={logo} alt="DataHelix Logo" className="h-10 w-auto opacity-90 hover:opacity-100 transition-opacity" />
            </Link>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 md:hidden">Welcome Back</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8 md:hidden">Sign in to your account</p>

          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 hidden md:block">Sign In</h3>

          {/* Redirection Callout */}
          {isRedirected && (
            <div className="bg-orange-50 dark:bg-[#f97316]/10 border border-orange-200 dark:border-[#f97316]/30 text-orange-800 dark:text-orange-200 p-4 rounded-lg mb-6 flex items-start text-sm">
              <FaLock className="mt-1 mr-3 flex-shrink-0 text-orange-600 dark:text-orange-400" size={14} />
              <div>
                <p className="font-semibold mb-1">Account Required</p>
                <p className="opacity-90 dark:opacity-80">Please login to access this content.</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-200 p-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-gray-400 text-xs font-mono mb-2 uppercase" htmlFor="email">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                placeholder="Enter Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-3 bg-gray-50 dark:bg-[#0a1220] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-gray-400 text-xs font-mono uppercase" htmlFor="password">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs text-blue-400 hover:text-blue-300 transition-colors">Forgot?</Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Your Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 bg-gray-50 dark:bg-[#0a1220] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                >
                  {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </button>
              </div>
            </div>

            {/* Captcha: Always render. 
                "interaction-only" means it is invisible unless a challenge is required.
                This allows us to pre-fetch a token for seamless login, and show the widget if a challenge is needed. */}
            <div className="flex justify-center pt-2">
              <Turnstile
                key={turnstileKey}
                ref={turnstileRef}
                sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                onVerify={(token) => {
                  setCaptchaToken(token);
                  if (error.includes("Security")) setError(""); // Clear security error on success
                }}
                onError={() => setError("CAPTCHA failed. Please try again.")}
                onExpire={() => setCaptchaToken(null)}
                theme="dark"
                appearance={showCaptcha ? "always" : "interaction-only"}
              />
            </div>

            <button
              type="submit"
              disabled={loading || (showCaptcha && !captchaToken)}
              className={`w-full py-3 rounded-lg text-white font-medium shadow-lg shadow-blue-500/20 ${loading || (showCaptcha && !captchaToken)
                ? "bg-blue-600/70 cursor-wait opacity-70"
                : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 transform hover:-translate-y-0.5"
                } transition-all duration-200`}
            >
              {loading ? "Signing in..." : (showCaptcha && !captchaToken ? "Please Complete Security Check" : "Sign In")}
            </button>
          </form>

          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-gray-800"></div>
            <span className="px-4 text-gray-500 text-xs uppercase font-mono">Or continue with</span>
            <div className="flex-1 border-t border-gray-800"></div>
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full py-3 rounded-lg bg-white dark:bg-[#0a1220] border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-all flex items-center justify-center gap-3 font-medium shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Google
          </button>

          <p className="mt-8 text-center text-gray-400 text-sm">
            Don't have an account?{" "}
            <a href="/register" className="text-blue-400 hover:text-blue-300 font-medium hover:underline">
              Create Account
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
